import { types } from 'mobx-state-tree';
import { browserHistory } from 'react-router';
import { RouterStore, syncHistoryWithStore } from 'mobx-react-router';

import config from '../../config';
import RestTransport from '../../utils/transport';

import SessionStore from '../SessionStore';
import RoutingStore from '../RoutingStore';
import LocaleStore from '../LocaleStore';
import DiscoveryStore from '../DiscoveryStore';
import SettingsStore from '../SettingsStore';
import ToastsStore from '../ToastsStore';
import EnumsStore from '../EnumsStore';
import OrdersStore from '../OrdersStore';
import ExternalOrderStore from '../ExternalOrderStore';
import OnboardingStore from '../OnboardingStore';
import EbayOAuthConnection from '../common/EbayOAuthConnection';
import AzdConnection from '../common/AzdConnection';
import ListingsPageStore from '../ListingsPageStore';
import EducationStore from '../EducationStore';
import DashboardStore from '../DashboardStore';
import CatalogStore from '../CatalogStore';
import VMStore from '../VMStore';
import NotificationsStore from '../NotificationsStore';

const routerStore = new RouterStore();
const enhancedBrowserHistory = syncHistoryWithStore(browserHistory, routerStore);

const UIStore = types
  .model('UIStore', {
    /**
     * Session store to handle user's authentication
     * Will do actions like signup, login, forgot password,...
     * also perform read, write JWT token to cookie
     */
    sessionStore: types.optional(SessionStore, {}),
    /**
     * Settings store to handle the (single) user's settings
     */
    settingsStore: types.optional(SettingsStore, {}),
    /**
     * toasts store for handle the system toasts
     */
    toastsStore: types.optional(ToastsStore, {}),
    /**
     * discovery store to handle discovery from source
     * destination and store it for preview
     */
    discoveryStore: types.optional(DiscoveryStore, {
      discoveryId: null,
    }),
    ordersStore: types.optional(OrdersStore, {}),
    externalOrderStore: types.optional(ExternalOrderStore, {}),
    listingsPageStore: types.optional(ListingsPageStore, {}),
    /**
     * Axios instance to handle Rest API,
     * will reference to session store with token to authenticate
     */
    transport5007: types.optional(types.frozen, {}),
    transport3005: types.optional(types.frozen, {}),
    transport: types.optional(types.frozen, {}),
    transport7: types.optional(types.frozen, {}),

    /**
     * Routing store with enhanced brower history
     * Wrap react-router's browser history with Mobx to use Mobx observable power
     */
    routingStore: types.optional(RoutingStore, {
      browserHistory: enhancedBrowserHistory,
    }),

    /**
     * Locale store will handle i18n for our app
     * Parse locale user language and set locale messages data
     */
    localeStore: types.optional(LocaleStore, {}),
    /**
     * enums store
     */
    enumsStore: types.optional(EnumsStore, {}),

    /**
     * Onboarding store takes care of the onboarding wizard
     */
    onboardingStore: types.optional(OnboardingStore, {}),

    /**
     * Used for the learning center page
     */
    educationStore: types.optional(EducationStore, {}),

    /**
     * Handles the ebay oauth connection
     */
    ebayOAuthConnection: types.optional(EbayOAuthConnection, {}),

    /**
     * Handles the amazon (as destination) connection
     */
    azdConnection: types.optional(AzdConnection, {}),

    /**
     * Handles the dashboard data
     */
    dashboardStore: types.optional(DashboardStore, {}),

    /**
     * Handles the catalog data
     */
    catalogStore: types.optional(CatalogStore, {}),

    /**
     * Handles VM TripleCloud API
     */
    vmStore: types.optional(VMStore, {}),
    /**
     * Handles Notifications
     */
    notificationsStore: types.optional(NotificationsStore, {}),
  })
  .actions(self => {
    const afterCreate = () => {
      self.transport3005 = new RestTransport({
        baseURL: config.API_URL3005,
        timeout: 1000000,
        token: self.sessionStore.token,
        accountCode: self.sessionStore.accountCode,
        handleUnauthorized: self.sessionStore.handleUnauthorized,
        handleNetworkError: self.sessionStore.handleNetworkError,
        handleNetworkNotDown: self.sessionStore.handleNetworkNotDown,
      });
      self.transport5007V2 = new RestTransport({
        baseURL: config.API_URL5007,
        timeout: 1000000,
        token: self.sessionStore.token,
        accountCode: self.sessionStore.accountCode,
        handleUnauthorized: self.sessionStore.handleUnauthorized,
        handleNetworkError: self.sessionStore.handleNetworkError,
        handleNetworkNotDown: self.sessionStore.handleNetworkNotDown,
      });
    };
    const initOnLoad = () => {
      const enums = self.enumsStore.getEnums();
      const enumsReverse = self.enumsStore.getEnumsReverse();
      const Translations = self.localeStore.initLocale();
      const initSession = self.sessionStore.initSession();
      return [enums, enumsReverse, Translations, initSession];
    };
    const createTransport = () => {
      self.transport = new RestTransport({
        baseURL: config.API_URL,
        timeout: 1000000,
        token: self.sessionStore.token,
        accountCode: self.sessionStore.accountCode,
        handleUnauthorized: self.sessionStore.handleUnauthorized,
        handleNetworkError: self.sessionStore.handleNetworkError,
        handleNetworkNotDown: self.sessionStore.handleNetworkNotDown,
      });
      self.transport5007 = new RestTransport({
        baseURL: config.API_URL5007,
        timeout: 1000000,
        token: self.sessionStore.token,
        accountCode: self.sessionStore.accountCode,
        handleUnauthorized: self.sessionStore.handleUnauthorized,
        handleNetworkError: self.sessionStore.handleNetworkError,
        handleNetworkNotDown: self.sessionStore.handleNetworkNotDown,
      });
      self.transport5008 = new RestTransport({
        baseURL: config.API_URL5008,
        timeout: 1000000,
        token: self.sessionStore.token,
        accountCode: self.sessionStore.accountCode,
        handleUnauthorized: self.sessionStore.handleUnauthorized,
        handleNetworkError: self.sessionStore.handleNetworkError,
        handleNetworkNotDown: self.sessionStore.handleNetworkNotDown,
      });
      self.transport3005 = new RestTransport({
        baseURL: config.API_URL3005,
        timeout: 1000000,
        token: self.sessionStore.token,
        accountCode: self.sessionStore.accountCode,
        handleUnauthorized: self.sessionStore.handleUnauthorized,
        handleNetworkError: self.sessionStore.handleNetworkError,
        handleNetworkNotDown: self.sessionStore.handleNetworkNotDown,
      });
      self.transport5007V2 = new RestTransport({
        baseURL: config.API_URL5007,
        timeout: 1000000,
        token: self.sessionStore.token,
        accountCode: self.sessionStore.accountCode,
        handleUnauthorized: self.sessionStore.handleUnauthorized,
        handleNetworkError: self.sessionStore.handleNetworkError,
        handleNetworkNotDown: self.sessionStore.handleNetworkNotDown,
      });
    };

    return {
      createTransport,
      afterCreate,
      initOnLoad,
    };
  });

export default UIStore;
